import { useMemo } from 'react'
import type { CustomFieldDefinitionDocument } from '@/lib/db/rxdb/hooks/entities/custom-field-definitions'
import type { CustomFieldTabDocument } from '@/lib/db/rxdb/hooks/entities/custom-field-tabs'
import type { CustomFieldGroupDocument } from '@/lib/db/rxdb/hooks/entities/custom-field-groups'
import type { CustomFieldValueDocument } from '@/lib/db/rxdb/hooks/entities/custom-field-values'
import {
  systemTabOverviewId,
  systemTabPropertiesId,
} from '@/lib/utils/systemTabIds'
import {
  useCustomFieldDefinitions,
  useCustomFieldGroups,
  useCustomFieldTabs,
  useCustomFieldValues,
} from '@/lib/db/rxdb/hooks'

/**
 * Synthetic tab id sentinels used when a field references a tab that hasn't been
 * pulled into RxDB yet (initial sync race) or has been orphaned by a bug. Mutations
 * MUST reject any save attempt where customFieldTabId starts with this prefix.
 */
export const PENDING_TAB_ID_PREFIX = '__pending'

const sortByOrder = <T extends { sortOrder?: string | null }>(arr: Array<T>): Array<T> =>
  [...arr].sort((a, b) => {
    const ao = a.sortOrder ?? ''
    const bo = b.sortOrder ?? ''
    if (ao === bo) return 0
    return ao < bo ? -1 : 1
  })

const isPendingTabId = (id: string): boolean => id.startsWith(PENDING_TAB_ID_PREFIX)

const buildSyntheticTab = (
  envId: string,
  kind: 'overview' | 'properties' | 'unsorted',
): CustomFieldTabDocument => {
  const base = {
    envId,
    isSystem: true,
    isDeleted: false,
    workItemTypeIds: [],
    projectIds: [],
    sortOrder: kind === 'overview' ? 'a0' : kind === 'properties' ? 'a1' : 'zzz',
    createdOn: 0,
    createdBy: '',
    updatedOn: 0,
    updatedBy: '',
  }
  if (kind === 'overview') {
    return {
      ...base,
      id: `${PENDING_TAB_ID_PREFIX}_overview__`,
      name: 'Overview',
      icon: 'Info',
      renderMode: 'inline',
    }
  }
  if (kind === 'properties') {
    return {
      ...base,
      id: `${PENDING_TAB_ID_PREFIX}_properties__`,
      name: 'Properties',
      icon: 'Settings',
      renderMode: 'tab',
    }
  }
  return {
    ...base,
    id: `${PENDING_TAB_ID_PREFIX}_unsorted__`,
    name: 'Unsorted',
    icon: 'Lock',
    renderMode: 'tab',
  }
}

/**
 * Resolves which custom fields apply to a given task based on its WIT and project,
 * organizes them by tab + group, applies tab scoping, and loads the corresponding values.
 *
 * Scoping rules (deliberately asymmetric — see Tabs vs Fields below):
 *
 *   FIELD scoping is **explicit opt-in**: empty workItemTypeIds AND empty projectIds
 *     ⇒ field is hidden everywhere. Populated arrays must match (AND when both populated).
 *
 *   TAB scoping uses the opposite default: empty arrays ⇒ tab is unrestricted. Populated
 *     arrays narrow with AND logic when both populated.
 *
 *   A field appears only if BOTH passes apply: its own field rule AND its tab's rule.
 *   Tabs with zero applicable fields auto-hide so wide-open tabs are harmless.
 *
 * Synthetic fallback: if a field references a tab that hasn't been pulled yet, the
 * field still renders under a synthetic tab object (see buildSyntheticTab) — the
 * synthetic id starts with PENDING_TAB_ID_PREFIX and is rejected by mutations.
 */
export function useCustomFieldsForTask(
  taskId: string | undefined,
  projectId: string | null | undefined,
  workItemTypeId: string | null | undefined,
  envId: string | null | undefined,
) {
  const { customFieldDefinitions, isLoading: defsLoading } = useCustomFieldDefinitions()
  const { customFieldTabs, isLoading: tabsLoading } = useCustomFieldTabs()
  const { customFieldGroups, isLoading: groupsLoading } = useCustomFieldGroups()
  const { customFieldValues, isLoading: valsLoading } = useCustomFieldValues(
    taskId ? { taskId } : undefined,
  )

  const applicableFields = useMemo<Array<CustomFieldDefinitionDocument>>(() => {
    if (!customFieldDefinitions) return []

    return customFieldDefinitions.filter((def) => {
      if (def.isDeleted) return false
      const hasWitScope = def.workItemTypeIds.length > 0
      const hasProjScope = def.projectIds.length > 0

      // Field scoping is explicit opt-in.
      if (!hasWitScope && !hasProjScope) return false

      const witMatch =
        !hasWitScope || (workItemTypeId != null && def.workItemTypeIds.includes(workItemTypeId))
      const projMatch =
        !hasProjScope || (projectId != null && def.projectIds.includes(projectId))
      return witMatch && projMatch
    })
  }, [customFieldDefinitions, workItemTypeId, projectId])

  const tabPassesScoping = (tab: CustomFieldTabDocument): boolean => {
    if (tab.isDeleted) return false
    const hasWitScope = tab.workItemTypeIds.length > 0
    const hasProjScope = tab.projectIds.length > 0
    // Tab scoping: empty = unrestricted (opposite of field scoping).
    if (!hasWitScope && !hasProjScope) return true
    const witMatch =
      !hasWitScope || (workItemTypeId != null && tab.workItemTypeIds.includes(workItemTypeId))
    const projMatch =
      !hasProjScope || (projectId != null && tab.projectIds.includes(projectId))
    return witMatch && projMatch
  }

  const valueMap = useMemo(() => {
    const map = new Map<string, CustomFieldValueDocument>()
    if (!customFieldValues) return map
    for (const val of customFieldValues) {
      map.set(val.customFieldDefinitionId, val)
    }
    return map
  }, [customFieldValues])

  const structured = useMemo(() => {
    const realTabsById = new Map<string, CustomFieldTabDocument>()
    for (const t of customFieldTabs ?? []) {
      if (!t.isDeleted) realTabsById.set(t.id, t)
    }

    const groupsByTab = new Map<string, Array<CustomFieldGroupDocument>>()
    for (const g of customFieldGroups ?? []) {
      if (g.isDeleted) continue
      const list = groupsByTab.get(g.customFieldTabId) ?? []
      list.push(g)
      groupsByTab.set(g.customFieldTabId, list)
    }

    // Bucket fields by the tab they reference; if the tab doesn't resolve, attach a synthetic.
    const fieldsByTabId = new Map<string, Array<CustomFieldDefinitionDocument>>()
    const syntheticTabsById = new Map<string, CustomFieldTabDocument>()

    const overviewId = envId ? systemTabOverviewId(envId) : null
    const propertiesId = envId ? systemTabPropertiesId(envId) : null

    for (const f of applicableFields) {
      const tabId = f.customFieldTabId
      let resolvedTabId = tabId
      if (!realTabsById.has(tabId)) {
        let synthetic: CustomFieldTabDocument
        if (envId && tabId === overviewId) {
          synthetic = buildSyntheticTab(envId, 'overview')
        } else if (envId && tabId === propertiesId) {
          synthetic = buildSyntheticTab(envId, 'properties')
        } else if (envId) {
          synthetic = buildSyntheticTab(envId, 'unsorted')
        } else {
          continue
        }
        syntheticTabsById.set(synthetic.id, synthetic)
        resolvedTabId = synthetic.id
      }
      const list = fieldsByTabId.get(resolvedTabId) ?? []
      list.push(f)
      fieldsByTabId.set(resolvedTabId, list)
    }

    // Render only tabs that pass scoping AND have at least one applicable field.
    // Sort tabs first (using their own sortOrder), THEN build the (tab, ungrouped, groups) buckets.
    const allTabs = [...realTabsById.values(), ...syntheticTabsById.values()]
    const visibleTabs = sortByOrder(
      allTabs
        .filter((tab) => tabPassesScoping(tab))
        .filter((tab) => (fieldsByTabId.get(tab.id)?.length ?? 0) > 0),
    )

    const tabBuckets = visibleTabs.map((tab) => {
      const fieldsForTab = fieldsByTabId.get(tab.id) ?? []
      const tabGroups = sortByOrder(groupsByTab.get(tab.id) ?? [])
      const groups = tabGroups.map((group) => {
        const groupFields = sortByOrder(
          fieldsForTab.filter((f) => f.customFieldGroupId === group.id),
        )
        return { group, fields: groupFields }
      })
      const ungrouped = sortByOrder(
        fieldsForTab.filter(
          (f) => f.customFieldGroupId == null || !tabGroups.some((g) => g.id === f.customFieldGroupId),
        ),
      )
      return { tab, ungrouped, groups }
    })

    const inlineFields = tabBuckets
      .filter(({ tab }) => tab.renderMode === 'inline')
      .flatMap(({ ungrouped, groups }) => [
        ...ungrouped,
        ...groups.flatMap((g) => g.fields),
      ])

    const tabRenderEntries = tabBuckets.filter(({ tab }) => tab.renderMode === 'tab')

    return { tabRenderEntries, inlineFields, allTabs }
  }, [applicableFields, customFieldTabs, customFieldGroups, envId, workItemTypeId, projectId])

  return {
    /** Bucketed tabs to render in the task detail tab strip (renderMode === 'tab'). */
    tabs: structured.tabRenderEntries,
    /** Fields to render inline in the sidebar (flattened across all renderMode === 'inline' tabs). */
    inlineFields: structured.inlineFields,
    /** Map of fieldDefId → CustomFieldValueDocument. */
    valueMap,
    isLoading: defsLoading || tabsLoading || groupsLoading || valsLoading,
  }
}

export { isPendingTabId }

/**
 * Batch-loads custom field values for multiple tasks (for list view).
 * Returns Map<taskId, Map<fieldDefId, CustomFieldValueDocument>>
 */
export function useCustomFieldValuesForTasks(taskIds: Array<string>) {
  const { customFieldValues, isLoading } = useCustomFieldValues()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableTaskIds = useMemo(() => new Set(taskIds), [taskIds.join(',')])

  const batchMap = useMemo(() => {
    const map = new Map<string, Map<string, CustomFieldValueDocument>>()
    if (!customFieldValues || stableTaskIds.size === 0) return map

    for (const val of customFieldValues) {
      if (!stableTaskIds.has(val.taskId)) continue
      let fieldMap = map.get(val.taskId)
      if (!fieldMap) {
        fieldMap = new Map()
        map.set(val.taskId, fieldMap)
      }
      fieldMap.set(val.customFieldDefinitionId, val)
    }
    return map
  }, [customFieldValues, stableTaskIds])

  return { batchValueMap: batchMap, isLoading }
}

/**
 * Returns all custom field definitions applicable to a given project + WIT combo.
 * Useful for determining which columns to show in a task list.
 */
export function useApplicableCustomFieldDefinitions(
  projectId: string | null | undefined,
  workItemTypeId?: string | null,
): {
  definitions: Array<CustomFieldDefinitionDocument>
  isLoading: boolean
} {
  const { customFieldDefinitions, isLoading } = useCustomFieldDefinitions()

  const definitions = useMemo(() => {
    if (!customFieldDefinitions) return []

    return customFieldDefinitions.filter((def) => {
      if (def.isDeleted) return false
      const hasWitScope = def.workItemTypeIds.length > 0
      const hasProjScope = def.projectIds.length > 0

      if (!hasWitScope && !hasProjScope) return false

      const projMatch =
        !hasProjScope || (projectId != null && def.projectIds.includes(projectId))
      if (workItemTypeId !== undefined) {
        const witMatch =
          !hasWitScope || (workItemTypeId != null && def.workItemTypeIds.includes(workItemTypeId))
        return witMatch && projMatch
      }
      return projMatch
    })
  }, [customFieldDefinitions, projectId, workItemTypeId])

  return { definitions, isLoading }
}

/**
 * Hook for the settings field-form: returns ONLY real (non-synthetic) tabs/groups
 * that are safe to assign as a field's customFieldTabId / customFieldGroupId. Never
 * exposes synthetic fallback tabs — assignment must never write a sentinel id.
 *
 * Three explicit states for the consumer:
 *   - state A loading:  isLoading === true                                     (show spinner, disable Save)
 *   - state B happy:    isLoading === false && tabsForAssignment.length > 0    (normal path)
 *   - state C error:    isLoading === false && tabsForAssignment.length === 0  (block Save with banner)
 */
export function useCustomFieldTabsForAssignment(): {
  tabsForAssignment: Array<CustomFieldTabDocument>
  groupsForAssignment: Array<CustomFieldGroupDocument>
  isLoading: boolean
} {
  const { customFieldTabs, isLoading: tabsLoading } = useCustomFieldTabs()
  const { customFieldGroups, isLoading: groupsLoading } = useCustomFieldGroups()

  const tabsForAssignment = useMemo<Array<CustomFieldTabDocument>>(() => {
    if (!customFieldTabs) return []
    return sortByOrder(customFieldTabs.filter((t) => !t.isDeleted && !isPendingTabId(t.id)))
  }, [customFieldTabs])

  const groupsForAssignment = useMemo<Array<CustomFieldGroupDocument>>(() => {
    if (!customFieldGroups) return []
    return sortByOrder(customFieldGroups.filter((g) => !g.isDeleted))
  }, [customFieldGroups])

  return {
    tabsForAssignment,
    groupsForAssignment,
    isLoading: tabsLoading || groupsLoading,
  }
}
